import React from "react";
import PropTypes from 'prop-types';
import { Box, Chip, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import Tooltip from '../Shared/CopyableTooltip';
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import ArchiveOutlined from "@mui/icons-material/ArchiveOutlined";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import MarkEmailReadOutlined from "@mui/icons-material/MarkEmailReadOutlined";
import MarkEmailUnreadOutlined from "@mui/icons-material/MarkEmailUnreadOutlined";
import UnarchiveOutlined from "@mui/icons-material/UnarchiveOutlined";

function NotifTooltip({ title, children }) {
  return (
    <Tooltip title={title} placement="top" arrow copyable={false}>
      <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle" }}>
        {children}
      </Box>
    </Tooltip>
  );
}

NotifTooltip.propTypes = {
  title: PropTypes.node,
  children: PropTypes.node,
};

export default function DialogNotifiche({
  open,
  onClose,
  notification,
  typeLabel,
  variant,
  onToggleRead,
  onArchive,
  onDelete,
  onRestore,
  readOnly = false,
}) {
  if (!notification) return null;

  const isArchived = variant === "archived";
  const unread = !notification.isRead && !isArchived;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: "20px",
            overflow: "hidden",
            border: "1px solid #e2e8f0",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          pb: 1,
          pt: 2.5,
          px: 2.5,
          borderBottom: "1px solid #eee",
        }}
      >
        <Box minWidth={0}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
            <Typography
              sx={{
                fontFamily: "inherit",
                fontSize: 12,
                color: "#808080",
              }}
            >
              {notification.date}
            </Typography>
            {typeLabel ? (
              <Chip
                label={typeLabel}
                size="small"
                variant="outlined"
                sx={{
                  height: 22,
                  fontSize: 11,
                  fontWeight: 700,
                  borderColor: "#0b3d87",
                  color: "#0b3d87",
                }}
              />
            ) : null}
          </Box>
          <Typography
            sx={{
              fontFamily: "inherit",
              fontWeight: unread ? "bold" : 600,
              color: "#0b3d87",
              fontSize: 20,
              lineHeight: 1.3,
              pr: 1,
              wordBreak: "break-word",
            }}
          >
            {notification.title}
          </Typography>
        </Box>
        <NotifTooltip title="Chiudi">
          <IconButton aria-label="Chiudi" onClick={onClose} size="small" sx={{ mt: -0.5 }}>
            <CloseOutlined sx={{ color: "#0b3d87" }} />
          </IconButton>
        </NotifTooltip>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, pb: 2, pt: 2 }}>
        <Typography
          sx={{
            fontFamily: "inherit",
            color: "#333",
            fontSize: 15,
            lineHeight: 1.6,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {notification.description}
        </Typography>

        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={1}
          flexWrap="wrap"
          mt={3}
          pt={2}
          borderTop="1px solid #eee"
        >
          {isArchived ? (
            <>
              <NotifTooltip title="Ripristina la notifica nell'elenco principale">
                <IconButton
                  aria-label="Ripristina notifica"
                  disabled={readOnly}
                  onClick={() => {
                    onRestore(notification.id);
                    onClose();
                  }}
                >
                  <UnarchiveOutlined sx={{ color: "#0b3d87", fontSize: 28 }} />
                </IconButton>
              </NotifTooltip>
              <NotifTooltip title="Elimina la notifica">
                <IconButton
                  aria-label="Elimina notifica"
                  disabled={readOnly}
                  onClick={() => {
                    onDelete(notification.id);
                    onClose();
                  }}
                >
                  <DeleteOutline sx={{ color: "#e00036", fontSize: 28 }} />
                </IconButton>
              </NotifTooltip>
            </>
          ) : (
            <>
              <NotifTooltip title={notification.isRead ? "Segna come non letta" : "Segna come letta"}>
                <IconButton
                  aria-label={notification.isRead ? "Segna come non letta" : "Segna come letta"}
                  disabled={readOnly}
                  onClick={() => onToggleRead(notification.id)}
                >
                  {notification.isRead ? (
                    <MarkEmailUnreadOutlined sx={{ color: "#0b3d87", fontSize: 28 }} />
                  ) : (
                    <MarkEmailReadOutlined sx={{ color: "#0b3d87", fontSize: 28 }} />
                  )}
                </IconButton>
              </NotifTooltip>
              <NotifTooltip title="Archivia la notifica">
                <IconButton
                  aria-label="Archivia notifica"
                  disabled={readOnly}
                  onClick={() => {
                    onArchive(notification.id);
                    onClose();
                  }}
                >
                  <ArchiveOutlined sx={{ color: "#ed6c02", fontSize: 28 }} />
                </IconButton>
              </NotifTooltip>
              <NotifTooltip title="Elimina la notifica">
                <IconButton
                  aria-label="Elimina notifica"
                  disabled={readOnly}
                  onClick={() => {
                    onDelete(notification.id);
                    onClose();
                  }}
                >
                  <DeleteOutline sx={{ color: "#e00036", fontSize: 28 }} />
                </IconButton>
              </NotifTooltip>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

DialogNotifiche.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  notification: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    description: PropTypes.string,
    date: PropTypes.string,
    isRead: PropTypes.bool,
  }),
  typeLabel: PropTypes.string,
  variant: PropTypes.string,
  onToggleRead: PropTypes.func.isRequired,
  onArchive: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onRestore: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};
 